import { useLocation, useNavigate } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import { ExclamationCircleIcon, PlusCircleIcon } from '@heroicons/react/20/solid';
import { DocumentCheckIcon, UsersIcon } from '@heroicons/react/24/outline';
import { validatePolicySync } from 'shared';

import { useGroups } from '../../hooks/useGroups';
import { useGetSelectedOrganisation } from '../../hooks/useGetSelectedOrganisation';
import { useCreateGroupMutation } from '../../services/api/groupApi/group';
import { classNames } from '../../../common/lib/classNames';
import { Badge } from '../../../common/Atoms/Badge';

// Nested sidebar content for the group pages: every group in the org (the
// main sidebar only shows the default one) plus a shortcut to create a new
// group.
export function GroupsNav() {
  const { data: groups, refetch } = useGroups();
  const currentOrg = useGetSelectedOrganisation();
  const [createGroup] = useCreateGroupMutation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // /:orgId/groups/:groupId[/...]
  const activeGroupId = Number(pathname.split(`/`).filter(Boolean)[2]);

  const sortedGroups = [...(groups ?? [])]
    .sort((a, b) => Number(b.default) - Number(a.default));

  async function onNewGroup() {
    const res = await createGroup({ organisationId: currentOrg.id }).unwrap();

    const createdId = res?.id;

    if (refetch) {
      // Force ensure the group is in the cache
      await refetch();
    }

    if (createdId) {
      navigate(`/${currentOrg.id}/groups/${createdId}`);
    }
  }

  return (
    <div className={ `px-2` }>
      <p className={ `px-2 text-xs font-semibold uppercase tracking-wider text-gray-400` }>
        { `Group Workflows` }
      </p>
      <div className={ `mt-1 space-y-1` }>
        { sortedGroups.map(group => {
          const invalidCount = group.policies?.filter(p => {
            try {
              validatePolicySync(p);

              return false;
            }
            catch (e) {
              return true;
            }
          }).length;
          const validCount = (group.policies?.length ?? 0) - (invalidCount ?? 0);

          return (
            <button
              key={ group.id }
              type={ `button` }
              onClick={ () => navigate(`/${currentOrg?.id}/groups/${group.id}`) }
              className={ classNames(
                `flex w-full flex-col rounded-md px-2 py-1.5 text-left text-sm`,
                group.id === activeGroupId
                  ? `bg-gray-100 font-medium text-gray-900`
                  : `text-gray-600 hover:bg-gray-100 hover:text-gray-900`,
              ) }
            >
              <span className={ `flex w-full items-center` }>
                <span className={ `flex-1 truncate` }>{ group.title }</span>
                { group.default && (
                  <Badge
                    color={ `blue` }
                    message={ `Default` }
                    size={ `xs` }
                  />
                ) }
              </span>
              <span className={ `mt-0.5 flex` }>
                { group.contactsCount !== undefined && (
                  <Tooltip title={ `Group contains ${group.contactsCount} contacts` }>
                    <span className={ `mx-1 flex text-xs` }>
                      { group.contactsCount }
                      <UsersIcon className={ `ml-0.5 h-4 w-4 text-gray-400` } />
                    </span>
                  </Tooltip>
                ) }
                { group.policies !== undefined && (
                  <Tooltip title={ `Group contains ${validCount} policies` }>
                    <span className={ `mx-1 flex text-xs` }>
                      { validCount }
                      <DocumentCheckIcon className={ `ml-0.5 h-4 w-4 text-gray-400` } />
                    </span>
                  </Tooltip>
                ) }
                { !!invalidCount && (
                  <Tooltip title={ `Group has ${invalidCount} incomplete policies` }>
                    <span className={ `mx-1 flex text-xs text-red-700` }>
                      { invalidCount }
                      <ExclamationCircleIcon className={ `ml-0.5 h-4 w-4 text-red-700` } />
                    </span>
                  </Tooltip>
                ) }
              </span>
            </button>
          );
        }) }
        <button
          type={ `button` }
          onClick={ onNewGroup }
          className={ `flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900` }
        >
          <PlusCircleIcon className={ `mr-1.5 h-4 w-4 text-gray-400` } />
          { `Add new group` }
        </button>
      </div>
    </div>
  );
}
